// packages/web/src/components/ReportViewer.tsx — misma lógica (audios, revisiones, submitReview, remedial).
// La vista del alumno se delega a ./learner/LearnerReport (ver INTEGRACION.md).
// Cambios en la vista docente: encabezado con estado + puntaje + confianza, columna derecha fija con
// la revisión docente (siguiente acción), contenido en pestañas (Resumen · Desglose · Transcripción · Audios).
import { useEffect, useState } from 'react';
import { CheckCircle2, ArrowLeft, Volume2, ThumbsUp, Pencil, RotateCcw, Target, AlertTriangle, UserCircle2, BookOpen } from 'lucide-react';
import { DEFAULT_MASTERY_THRESHOLD, shouldTriggerRemedial } from '@marzano/contracts';
import { api, type AudioRef, type TeacherReviewRecord } from '../api';
import { Alert, Button, INPUT_CLASS, PageHeader, StatusBadge } from './ui';
import { RemedialTaskView } from './RemedialTaskView';
import { ScoreBreakdown } from './ScoreBreakdown';
import { LearnerReport } from './learner/LearnerReport';
import { ALL_DIMS, DICTAMEN_LABEL, DIM_LABEL, type Dictamen, type ChatTurn, type Dim } from '../types';

interface ReportViewerProps {
  dictamen: Dictamen;
  chatHistory: ChatTurn[];
  studentName: string;
  oaCodigo: string;
  hitoNombre: string;
  sessionId: string;
  siteId: string;
  onBackToList: () => void;
  viewerRole?: 'teacher' | 'learner';
  masteryThreshold?: number;
}

const STATE_TONE: Record<Dictamen['state'], { tone: 'danger' | 'warn' | 'ok' | 'info'; ring: string }> = {
  no_evidencia: { tone: 'danger', ring: 'border-red-200' },
  en_desarrollo: { tone: 'warn', ring: 'border-amber-200' },
  suficiente: { tone: 'ok', ring: 'border-emerald-200' },
  profundo: { tone: 'info', ring: 'border-marzano-200' },
};
const DIM_STYLE: Record<Dim, { n: number; bar: string; text: string }> = {
  recuperacion: { n: 1, bar: 'bg-[#2B5C9E]', text: 'text-[#2B5C9E]' },
  comprension: { n: 2, bar: 'bg-[#0B7A72]', text: 'text-[#0B7A72]' },
  analisis: { n: 3, bar: 'bg-[#6A47A6]', text: 'text-[#6A47A6]' },
  transferencia: { n: 4, bar: 'bg-[#A0530B]', text: 'text-[#A0530B]' },
};
const DECISION_LABEL: Record<TeacherReviewRecord['decision'], string> = {
  accepted: 'Aceptado', modified: 'Marcado para modificar', rerun_requested: 'Re-evaluación pedida',
} as Record<TeacherReviewRecord['decision'], string>;
type Tab = 'resumen' | 'desglose' | 'transcripcion' | 'audios';

export function ReportViewer(props: ReportViewerProps) {
  const { dictamen } = props;
  const isTeacher = (props.viewerRole ?? 'teacher') === 'teacher';
  const dimScores: Record<string, number> = {
    recuperacion: dictamen.dim_recuperacion, comprension: dictamen.dim_comprension, analisis: dictamen.dim_analisis, transferencia: dictamen.dim_transferencia,
  };
  const [audioBySeq, setAudioBySeq] = useState<Map<number, AudioRef>>(new Map());
  const [reviews, setReviews] = useState<TeacherReviewRecord[]>([]);
  const [reviewDecision, setReviewDecision] = useState<TeacherReviewRecord['decision'] | null>(null);
  const [reviewObs, setReviewObs] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewErr, setReviewErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('resumen');

  useEffect(() => {
    if (!isTeacher) return;
    let cancelled = false;
    void api.getAudioRefs(props.sessionId, props.siteId).then(({ audios }) => {
      if (cancelled) return;
      const m = new Map<number, AudioRef>();
      for (const a of audios) m.set(a.sequence_in_session, a);
      setAudioBySeq(m);
    }).catch(() => {});
    void api.getReviews(props.sessionId, props.siteId).then(({ reviews }) => { if (!cancelled) setReviews(reviews); }).catch(() => {});
    return () => { cancelled = true; };
  }, [props.sessionId, props.siteId, isTeacher]);

  async function submitReview(decision: TeacherReviewRecord['decision']) {
    setReviewBusy(true); setReviewErr(null);
    try {
      const { review } = await api.submitReview(props.sessionId, props.siteId, { decision, observations: reviewObs.trim() || null });
      setReviews((prev) => [review, ...prev]);
      setReviewDecision(null); setReviewObs('');
      if (decision === 'accepted') props.onBackToList();
    } catch (e) {
      setReviewErr(e instanceof Error ? e.message : 'Error al guardar la revisión');
    } finally {
      setReviewBusy(false);
    }
  }
  const reviewLocked = reviews.length > 0;

  if (!isTeacher) {
    return <LearnerReport dictamen={dictamen} studentName={props.studentName} sessionId={props.sessionId}
      masteryThreshold={props.masteryThreshold ?? DEFAULT_MASTERY_THRESHOLD} onBackToList={props.onBackToList} />;
  }

  const st = STATE_TONE[dictamen.state];
  const gaps = dictamen.gaps_detected;
  const recs = dictamen.recommendations;
  const tabs: { v: Tab; l: string; count?: number }[] = [
    { v: 'resumen', l: 'Resumen' },
    { v: 'desglose', l: 'Desglose del puntaje' },
    { v: 'transcripcion', l: 'Transcripción', count: props.chatHistory.length },
    ...(audioBySeq.size > 0 ? [{ v: 'audios' as Tab, l: 'Audios', count: audioBySeq.size }] : []),
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        back={{ label: 'Volver', onClick: props.onBackToList }}
        eyebrow={<>{props.oaCodigo} · {props.hitoNombre}</>}
        title={props.studentName}
        actions={<span className="text-[13px] text-slate-600 inline-flex items-center gap-1.5"><CheckCircle2 aria-hidden className="w-4 h-4 text-emerald-700" />Reporte emitido {new Date(dictamen.ai_emitted_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}</span>}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
        <div className="space-y-5 min-w-0">
          {/* Resumen superior */}
          <section className={`rounded-xl border-2 ${st.ring} bg-white p-5 grid grid-cols-1 md:grid-cols-[auto_minmax(0,1fr)] gap-6`}>
            <div className="flex flex-col gap-2 md:pr-6 md:border-r md:border-slate-200">
              <span className="text-[13px] text-slate-600">Puntaje global</span>
              <span className="text-5xl font-bold tabular-nums text-slate-900 leading-none">{dictamen.global_score.toFixed(1)}<span className="text-lg font-normal text-slate-500"> /100</span></span>
              <StatusBadge tone={st.tone}>{DICTAMEN_LABEL[dictamen.state]}</StatusBadge>
              <span className="text-[13px] text-slate-600">Confianza {(dictamen.confidence * 100).toFixed(0)}%</span>
            </div>
            <ul className="space-y-3 min-w-0">
              {ALL_DIMS.map((dim) => {
                const score = dimScores[dim] ?? 0;
                const S = DIM_STYLE[dim];
                const count = dictamen.evidence_count_by_dim[dim] ?? 0;
                return (
                  <li key={dim} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                    <span className="text-sm"><span className={`font-semibold ${S.text}`}>{S.n} · {DIM_LABEL[dim]}</span> <span className="text-[13px] text-slate-600">peso {dictamen.weights_marzano[dim]}% · {count} evidencia{count === 1 ? '' : 's'}</span></span>
                    <span className="text-sm font-semibold tabular-nums text-slate-900">{score.toFixed(1)}</span>
                    <span className="col-span-2 h-2 rounded-full bg-slate-100" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(score)} aria-label={`${DIM_LABEL[dim]} ${score.toFixed(1)}`}>
                      <span className={`block h-2 rounded-full ${S.bar}`} style={{ width: `${Math.min(100, Math.max(0, score))}%` }} />
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          {dictamen.total_penalty_pct !== 0 && (
            <Alert kind="warn">Penalización total por ayudas: <strong>{dictamen.total_penalty_pct}%</strong>. Se aplica a cada dimensión y llega al global por la ponderación Marzano (DEC-11).</Alert>
          )}

          <div role="tablist" aria-label="Secciones del reporte" className="flex gap-1 border-b border-slate-200 overflow-x-auto">
            {tabs.map((t) => (
              <button key={t.v} type="button" role="tab" aria-selected={tab === t.v} onClick={() => setTab(t.v)}
                className={`-mb-px whitespace-nowrap px-3.5 py-2.5 text-sm border-b-2 ${tab === t.v ? 'border-marzano-600 font-semibold text-marzano-700' : 'border-transparent text-slate-600 hover:text-slate-900'} focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500`}>
                {t.l}{t.count !== undefined && <span className="ml-1.5 text-xs text-slate-500 tabular-nums">{t.count}</span>}
              </button>
            ))}
          </div>

          {tab === 'resumen' && (
            <div className="space-y-5">
              {gaps && (gaps.gaps.length > 0 || gaps.common_errors_observed.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {gaps.gaps.length > 0 && <ListCard icon={<Target aria-hidden className="w-4 h-4 text-amber-700" />} title="Brechas detectadas" items={gaps.gaps} />}
                  {gaps.common_errors_observed.length > 0 && <ListCard icon={<AlertTriangle aria-hidden className="w-4 h-4 text-red-700" />} title="Errores conceptuales observados" items={gaps.common_errors_observed} />}
                </div>
              )}
              {recs && (recs.student.length > 0 || recs.teacher.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recs.teacher.length > 0 && <ListCard icon={<Pencil aria-hidden className="w-4 h-4 text-marzano-600" />} title="Recomendaciones para ti" items={recs.teacher} />}
                  {recs.student.length > 0 && <ListCard icon={<UserCircle2 aria-hidden className="w-4 h-4 text-emerald-700" />} title="Recomendaciones para el alumno" items={recs.student} />}
                </div>
              )}
              {dictamen.weights_justification && (
                <details className="group rounded-xl border border-slate-200 bg-white">
                  <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-slate-900">Por qué se ajustaron los pesos <span className="font-normal text-slate-600">· justificación curricular</span></summary>
                  <p className="px-4 pb-4 text-sm text-slate-700 leading-relaxed">{dictamen.weights_justification}</p>
                </details>
              )}
              {shouldTriggerRemedial(dictamen.state) && (
                <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><BookOpen aria-hidden className="w-4 h-4 text-marzano-600" />Guía remedial</h3>
                  <RemedialTaskView sessionId={props.sessionId} />
                </section>
              )}
            </div>
          )}

          {tab === 'desglose' && <ScoreBreakdown sessionId={props.sessionId} />}

          {tab === 'transcripcion' && (
            props.chatHistory.length === 0 ? <p className="text-sm text-slate-600 py-6 text-center">Sin turnos registrados.</p> : (
              <ol className="space-y-2.5">
                {props.chatHistory.map((t, i) => {
                  const coach = t.role === 'coach';
                  return (
                    <li key={i} className={`max-w-[85%] rounded-xl px-4 py-2.5 ${coach ? 'bg-white border border-slate-200' : 'ml-auto bg-marzano-50 border border-marzano-100'}`}>
                      <div className="text-xs font-semibold text-slate-600 mb-0.5">{coach ? 'Profesor virtual' : 'Alumno'}{t.modality ? ` · ${t.modality === 'oral' ? 'voz' : 'texto'}` : ''}</div>
                      <p className="text-sm text-slate-900 whitespace-pre-wrap">{t.text}</p>
                    </li>
                  );
                })}
              </ol>
            )
          )}

          {tab === 'audios' && (
            <section className="space-y-3">
              <p className="text-[13px] text-slate-600 flex items-center gap-1.5"><Volume2 aria-hidden className="w-4 h-4" />Audios originales guardados por 12 meses.</p>
              <ul className="space-y-3">
                {Array.from(audioBySeq.values()).sort((a, b) => a.sequence_in_session - b.sequence_in_session).map((a) => (
                  <li key={a.event_id} className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-2">
                    <div className="text-[13px] text-slate-600 tabular-nums">Turno {a.sequence_in_session} · {new Date(a.occurred_at).toLocaleTimeString('es-CL')} · {a.duration_sec.toFixed(0)} s</div>
                    <audio controls preload="none" src={a.sas_url} className="w-full" />
                    {a.transcription_preview && <p className="text-[13px] text-slate-700">“{a.transcription_preview}{a.transcription_preview.length >= 120 ? '…' : ''}”</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Siguiente acción: revisión docente */}
        <aside aria-labelledby="rev-t" className="xl:sticky xl:top-6 rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <div>
            <h2 id="rev-t" className="text-base font-semibold text-slate-900">Tu revisión</h2>
            <p className="text-[13px] text-slate-600 mt-0.5">Cierra el ciclo del reporte. Tu decisión queda registrada y ayuda a calibrar el motor.</p>
          </div>
          {reviews.length > 0 && (
            <ul className="space-y-2">
              {reviews.map((rev) => (
                <li key={rev.id} className="rounded-lg bg-slate-50 px-3 py-2.5 text-[13px]">
                  <div className="flex items-center justify-between gap-2"><strong className="text-slate-900">{DECISION_LABEL[rev.decision] ?? rev.decision}</strong><span className="text-xs text-slate-600">{new Date(rev.reviewed_at).toLocaleDateString('es-CL')}</span></div>
                  {rev.observations && <p className="mt-1 text-slate-700">“{rev.observations}”</p>}
                </li>
              ))}
            </ul>
          )}
          {reviewLocked ? (
            <Alert kind="success">Esta sesión ya tiene una revisión registrada.</Alert>
          ) : (
            <>
              <div>
                <label htmlFor="rev-obs" className="block text-sm font-medium text-slate-800">Observaciones <span className="font-normal text-slate-600">(opcional)</span></label>
                <textarea id="rev-obs" className={INPUT_CLASS + ' resize-y'} rows={3} value={reviewObs} onChange={(e) => setReviewObs(e.target.value)} disabled={reviewBusy} />
              </div>
              <div className="flex flex-col gap-2">
                <Button disabled={reviewBusy && reviewDecision !== 'accepted'} loading={reviewBusy && reviewDecision === 'accepted'} leftIcon={<ThumbsUp aria-hidden className="w-4 h-4" />}
                  onClick={() => { setReviewDecision('accepted'); void submitReview('accepted'); }}>Aceptar reporte</Button>
                <Button variant="secondary" disabled={reviewBusy && reviewDecision !== 'modified'} loading={reviewBusy && reviewDecision === 'modified'} leftIcon={<Pencil aria-hidden className="w-4 h-4" />}
                  onClick={() => { setReviewDecision('modified'); void submitReview('modified'); }}>Marcar para modificar</Button>
                <Button variant="secondary" className="!text-amber-900 !border-amber-300 hover:!bg-amber-50" disabled={reviewBusy && reviewDecision !== 'rerun_requested'} loading={reviewBusy && reviewDecision === 'rerun_requested'} leftIcon={<RotateCcw aria-hidden className="w-4 h-4" />}
                  onClick={() => { setReviewDecision('rerun_requested'); void submitReview('rerun_requested'); }}>Pedir re-evaluación</Button>
              </div>
            </>
          )}
          {reviewErr && <Alert kind="error">{reviewErr}</Alert>}
          <Button variant="ghost" className="w-full" onClick={props.onBackToList} leftIcon={<ArrowLeft aria-hidden className="w-4 h-4" />}>Nueva evaluación</Button>
        </aside>
      </div>
    </div>
  );
}

function ListCard({ icon, title, items }: { icon: React.ReactNode; title: string; items: readonly string[] }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">{icon}{title}</h3>
      <ul className="space-y-1.5 text-sm text-slate-800 list-disc pl-4 marker:text-slate-400">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
    </section>
  );
}

